import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { ProductModal as F1ProductModal } from '@factorialco/factorial-one-react'
import { Spinner } from '@factorialco/factorial-one-react/dist/experimental'
import Upsell from '@factorialco/factorial-one-react/icons/app/Upsell'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import useCreateUpsellRequest from 'modules/upsell/hooks/useCreateUpsellRequest'
import { useGetUpsellingData } from 'modules/upselling_kit/hooks/useGetUpsellingData'
import { UpsellingKitModule } from 'modules/upselling_kit/types'
import { useDefaultMessages } from 'modules/upselling_kit/utils/defaultMessages'
import {
  handleUpsellingLearnMore,
  handleUpsellingOnClose,
  handleUpsellingVisibility,
} from 'modules/upselling_kit/utils/handlers'
import { moduleIcons } from 'modules/upselling_kit/utils/moduleIcons'

import { UpsellGuard } from '../UpsellGuard'

interface ProductModalProps extends UpsellingKitModule {
  modalTitle?: string
  title?: string
  isOpen: boolean
  onClose: () => void
  imageUrl?: string
  pageSource: string
  onRequest?: () => void
  openFromUpsellButton?: boolean
  image?: string
  benefits?: string[]
}

const ProductModalContent = ({
  moduleRequested,
  requestReason,
  featureRequested,
  pageSource,
  title,
  isOpen,
  onClose,
  requestSource,
  modalTitle,
  openFromUpsellButton,
  image,
  benefits,
}: ProductModalProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    moduleName,
    upsellModalDescription,
    upsellModalImage: defaultUpsellModalImage,
    upsellModalBenefits: defaultUpsellModalBenefits,
    moduleSlug,
    loading,
  } = useGetUpsellingData({
    moduleRequested,
    title,
  })

  const defaultMessages = useDefaultMessages()

  const { create: upsellCreate } = useCreateUpsellRequest()

  useEffect(() => {
    if (isOpen && !openFromUpsellButton) {
      handleUpsellingVisibility({
        moduleRequested,
        requestSource,
        component: 'product_modal',
      })
    } else if (isOpen && openFromUpsellButton) {
      track(trackingEvents.upsellingKit.openProductModal, {
        source: requestSource,
        module: moduleRequested,
        component: 'product_modal',
      })
    }
  }, [isOpen, moduleRequested, requestSource, openFromUpsellButton])

  if (loading) {
    return <Spinner />
  }

  const handleOnClose = () => {
    handleUpsellingOnClose({
      onClose,
      requestSource,
      moduleRequested,
      component: 'product_modal',
    })
  }

  const handleLearnMore = () => {
    handleUpsellingLearnMore({
      moduleRequested,
      requestSource,
      component: 'product_modal',
    })
    navigate(
      navigation.discover.marketplaceModules.item.fullPath({
        id: moduleSlug || '',
      })
    )
  }

  const handleUpsellCreateRequest = async () => {
    await upsellCreate({
      moduleRequested: moduleRequested,
      requestSource: requestSource,
      requestReason: requestReason,
      featureRequested: featureRequested,
      pageSource: pageSource,
    })
  }

  const buildPrimaryAction = () => {
    return {
      label: i18n.t('upselling_kit.button.request_information'),
      onClick: handleUpsellCreateRequest,
      variant: 'promote' as const,
      icon: Upsell,
    }
  }

  const buildSecondaryAction = () => {
    return {
      label: i18n.t('upselling_kit.button.learn_more'),
      onClick: handleLearnMore,
      variant: 'outline' as const,
    }
  }

  return (
    <F1ProductModal
      isOpen={isOpen}
      onClose={handleOnClose}
      modalTitle={modalTitle || moduleName || ''}
      modalIcon={moduleIcons(moduleRequested)}
      title={title || upsellModalDescription || ''}
      image={image || defaultUpsellModalImage || ''}
      benefits={benefits || defaultUpsellModalBenefits || []}
      primaryAction={buildPrimaryAction()}
      secondaryAction={buildSecondaryAction()}
      errorMessage={defaultMessages.error}
      successMessage={defaultMessages.success}
      loadingState={defaultMessages.loading}
      nextSteps={defaultMessages.nextSteps}
      closeLabel={defaultMessages.close}
    />
  )
}

const ProductModal = ({
  fallbackComponent,
  title,
  isOpen,
  onClose,
  modalTitle,
  openFromUpsellButton = true,
  image,
  benefits,
  ...upsellProps
}: ProductModalProps) => {
  return (
    <UpsellGuard {...upsellProps} fallbackComponent={fallbackComponent || null}>
      <ProductModalContent
        {...upsellProps}
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        modalTitle={modalTitle}
        openFromUpsellButton={openFromUpsellButton}
        image={image}
        benefits={benefits}
      />
    </UpsellGuard>
  )
}

export { ProductModal }
